import api from "@/services/api";
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";

export const fetchFeedbacks = createAsyncThunk(
  "feedback/fetchFeedbacks",
  async (page = 1, { rejectWithValue }) => {
    try {
      const response = await api.get(`admin/feedbacks-list?page=${page}`);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateVisibilityStatus = createAsyncThunk(
  "feedback/updateVisibilityStatus",
  async ({ id, newStatus }, { rejectWithValue }) => {
    try {
      const response = await api.post("admin/update-featured-status", {
        feedback_id: id,
        is_featured: newStatus ? 'yes' : 'no'
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchOrderFeedbacks = createAsyncThunk(
  "feedback/fetchOrderFeedbacks",
  async ({ rfq_id }, { rejectWithValue }) => {
    try {
      const response = await api.get(`admin/get-feedback?rfq_id=${rfq_id}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const storeFeedbackByAdmin = createAsyncThunk(
  "feedback/storeFeedbackByAdmin",
  async (formData, { rejectWithValue }) => {
    try {
      const response = await api.post(
        "admin/store-feedback-by-admin",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);


const feedbackSlice = createSlice({
  name: "feedback",
  initialState: {
    feedbacks: [],
    order_feedbacks:[],
    pagination: null,
    loading: false,
    error: null,
  },
  reducers: {
    resetStatus: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchFeedbacks.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchFeedbacks.fulfilled, (state, action) => {
        state.loading = false;
        
        const newFeedbacks = action.payload.data?.feedbacks || [];
        const pageRequested = action.meta.arg || 1; // Get the page number passed to the thunk

        // If it's page 1, replace the state (e.g., on initial load or refresh)
        // Otherwise, append the new items to the bottom of the list
        if (pageRequested === 1) {
          state.feedbacks = newFeedbacks;
        } else {
          state.feedbacks = [...state.feedbacks, ...newFeedbacks];
        }
        
        state.pagination = action.payload.data?.pagination || null;
      })
      .addCase(fetchFeedbacks.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchOrderFeedbacks.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchOrderFeedbacks.fulfilled, (state, action) => {
        state.loading = false;
        state.order_feedbacks = action.payload;
        state.error = null;
      })
      .addCase(fetchOrderFeedbacks.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      .addCase(updateVisibilityStatus.pending, (state) => {
        // Optional: you might not want to set global loading to true here 
        // so it doesn't trigger the main loading spinners during a tiny toggle update.
      })
      .addCase(updateVisibilityStatus.fulfilled, (state, action) => {
        const { id, newStatus } = action.meta.arg;
        const index = state.feedbacks.findIndex((f) => f.id === id);
        
        if (index !== -1) {
          state.feedbacks[index].is_featured = newStatus ? 'yes' : 'no';
          state.feedbacks[index].isVisibleToCustomers = newStatus;
        }
      })
      .addCase(updateVisibilityStatus.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(storeFeedbackByAdmin.pending, (state) => {
        state.loading = true;
      })
      .addCase(storeFeedbackByAdmin.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(storeFeedbackByAdmin.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { resetStatus } = feedbackSlice.actions;
export default feedbackSlice.reducer;